<template>
  <div class="vendor vendor-simple col-sm-12">
    <div class="vendor-title pointer" @click="toggleBlock">
      <strong>
        <span class="tc-title">{{ vendor.name }}</span>
        <span v-if="displayMore" class="chevron glyphicon glyphicon-chevron-up" aria-hidden="true"></span>
        <span v-else class="chevron glyphicon glyphicon-chevron-down" aria-hidden="true"></span>
      </strong>
      <div @click.stop class="right-container">
        <div v-if="hasLegIntToggle" class="switch-container">
          <label v-if="settings.iabLegitimateInterestToggle">{{ $t('legitimateInterest') }}</label>
          <pc-checkbox-button
            v-if="settings.consentButtonStyle === 'button'"
            v-model="vendor.legitimateInterest"
            :aria-label="vendor.label"
            :label-on="settings.labelBtOn"
            :label-off="settings.labelBtOff"
            @input="emitConsentChanged"
          ></pc-checkbox-button>
          <pc-checkbox-native
            v-else-if="settings.consentButtonStyle === 'checkbox'"
            :aria-label="$t('enableVendorAria', { label: stripHtml(vendor.label) })"
            v-model="vendor.legitimateInterest"
            @input="emitConsentChanged"
          ></pc-checkbox-native>
          <pc-leg-switch
            v-else
            v-model="vendor.legitimateInterest"
            :settings="settings"
            :label-on="settings.labelBtOn"
            :label-off="settings.labelBtOff"
            @input="emitConsentChanged"
          ></pc-leg-switch>
        </div>
        <div
          class="switch-container"
          v-if="!hasNoConsentToggle"
          :class="{ 'visibility-hidden': !hasConsentToggleVisible }"
        >
          <label v-if="settings.iabLegitimateInterestToggle">{{ $t('consent') }}</label>
          <pc-checkbox-button
            v-if="settings.consentButtonStyle === 'button'"
            v-model="vendor.consent"
            :label-on="settings.labelBtOn"
            :label-off="settings.labelBtOff"
            @input="emitConsentChanged"
          ></pc-checkbox-button>
          <pc-checkbox-native
            v-else-if="settings.consentButtonStyle === 'checkbox'"
            v-model="vendor.consent"
            @input="emitConsentChanged"
          ></pc-checkbox-native>
          <pc-leg-switch
            v-else
            v-model="vendor.consent"
            :settings="settings"
            :label-on="settings.labelBtOn"
            :label-off="settings.labelBtOff"
            @input="emitConsentChanged"
          ></pc-leg-switch>
        </div>
      </div>
    </div>
    <div class="vendor-details" v-if="displayMore">
      <div class="detail-margin">
        <strong class="policy-title">{{ $t('privacyPolicy') }}:</strong>
        <a class="vendor-policy tc-description" :href="vendor.policyUrl" target="_blank" rel="noopener noreferrer">
          {{ vendor.policyUrl }}
        </a>
      </div>
      <div v-if="vendor.purposes.length > 0">
        <strong>{{ $t('purposes') }}:</strong>
        <ul>
          <li v-for="purposeId in vendor.purposes" :key="'vp' + purposeId">
            {{ iab.vendorList.purposes[purposeId].name }}
          </li>
        </ul>
      </div>
      <div v-if="vendor.specialPurposes.length > 0">
        <strong>{{ $t('specialPurposes') }}:</strong>
        <ul>
          <li v-for="specialPurposeId in vendor.specialPurposes" :key="'vsp' + specialPurposeId">
            {{ iab.vendorList.specialPurposes[specialPurposeId].name }}
          </li>
        </ul>
      </div>
      <div v-if="vendor.features.length > 0">
        <strong>{{ $t('features') }}:</strong>
        <ul>
          <li v-for="featureId in vendor.features" :key="'vsp' + featureId">
            {{ iab.vendorList.features[featureId].name }}
          </li>
        </ul>
      </div>
      <div v-if="vendor.specialFeatures.length > 0">
        <strong>{{ $t('specialFeatures') }}:</strong>
        <ul>
          <li v-for="specialFeatureId in vendor.specialFeatures" :key="'vsp' + specialFeatureId">
            {{ iab.vendorList.specialFeatures[specialFeatureId].name }}
          </li>
        </ul>
      </div>
      <div v-if="vendor.legIntPurposes.length > 0">
        <strong>{{ $t('legitimateInterests') }}:</strong>
        <ul>
          <li v-for="legIntId in vendor.legIntPurposes" :key="'vli' + legIntId">
            {{ iab.vendorList.purposes[legIntId].name }}
          </li>
        </ul>
      </div>
      <div v-if="displayStorageType">
        <strong>{{ $t('storageType') }}:</strong>
        <ul>
          <li v-if="vendor.cookieMaxAgeSeconds >= 0">
            {{ $t('cookies') }}
          </li>
          <li v-if="vendor.usesNonCookieAccess">
            {{ $t('otherStorage') }}
          </li>
        </ul>
      </div>
      <div v-if="vendor.cookieMaxAgeSeconds >= 0">
        <strong>{{ $t('cookieMaxAge') }}:</strong>
        {{ humanizedCookieMaxAge }}
        <span v-if="vendor.cookieMaxAgeSeconds > 0">({{ vendor.cookieMaxAgeSeconds }} {{ $t('seconds') }})</span>
      </div>
      <div v-if="deviceStorageDisclosures">
        <div class="tc-storage-detail">{{ $t('storageDetails') }}:</div>
        <table class="table">
          <thead>
            <tr>
              <th>{{ $t('storageIdentifier') }}</th>
              <th>{{ $t('storageType') }}</th>
              <th>{{ $t('storageMaxAge') }}</th>
              <th>{{ $t('storageDomain') }}</th>
              <th>{{ $t('purposes') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="disclosure of deviceStorageDisclosures" :key="disclosure.identifier">
              <td>
                {{ disclosure.identifier }}
              </td>
              <td>
                {{ disclosure.type }}
              </td>
              <td>
                {{ humanizeDuration(disclosure.maxAgeSeconds) }}
                <span v-if="disclosure.maxAgeSeconds > 0"
                  ><br />({{ disclosure.maxAgeSeconds }} {{ $t('seconds') }})</span
                >
              </td>
              <td>
                {{ disclosure.domain }}
              </td>
              <td>
                <ul class="pl-0">
                  <li v-for="purposeId of disclosure.purposes" :key="'storage' + purposeId">
                    {{ iab.vendorList.purposes[purposeId].name }}
                  </li>
                </ul>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script>
import PcCheckboxButton from '@/components/forms/pc-checkbox-button';
import PcLegSwitch from '@/components/forms/pc-leg-switch';
import PcCheckboxNative from '@/components/forms/pc-checkbox-native';

import { i18nMixin } from 'shared/i18n/i18n-mixin';
import { eventEmitterMixin } from '@/common/messaging/event-emitter-mixin';
import humanizeDuration from 'humanize-duration';
import fetch from 'unfetch';
import { stripHtml } from '@/common/filters/strip-html';

export default {
  components: {
    PcCheckboxButton,
    PcLegSwitch,
    PcCheckboxNative,
  },
  mixins: [i18nMixin, eventEmitterMixin],
  props: {
    settings: {
      type: Object,
      default: () => {},
    },
    vendor: {
      type: Object,
      default: () => ({
        name: '',
      }),
    },
    iab: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      displayMore: false,
      deviceStorageDisclosures: null,
    };
  },
  computed: {
    hasLegIntToggle() {
      return (
        (this.settings.iabLegitimateInterestToggle || !this.hasConsentToggleVisible) &&
        this.vendor.legIntPurposes.length > 0
      );
    },
    hasConsentToggleVisible() {
      return this.vendor.purposes.length > 0;
    },
    hasNoConsentToggle() {
      return !this.settings.iabLegitimateInterestToggle && this.hasLegIntToggle;
    },
    humanizedCookieMaxAge() {
      return this.humanizeDuration(this.vendor.cookieMaxAgeSeconds);
    },
    displayStorageType() {
      return this.vendor.cookieMaxAgeSeconds >= 0 || this.vendor.usesNonCookieAccess === true;
    },
  },
  watch: {
    'vendor.consent': function(value) {
      if (
        !this.settings.iabLegitimateInterestToggle &&
        !this.hasLegIntToggle &&
        this.vendor.legIntPurposes.length > 0
      ) {
        this.vendor.legitimateInterest = value;
      }
    },
  },
  methods: {
    stripHtml,
    humanizeDuration(seconds) {
      if (seconds != null) {
        if (seconds === 0) {
          return this.$t('endOfSession');
        }
        return humanizeDuration(seconds * 1000, { language: this.$getLocale() });
      }
      return 'N/A';
    },
    toggleBlock() {
      if (this.vendor.deviceStorageDisclosureUrl && this.deviceStorageDisclosure == null) {
        fetch(this.vendor.deviceStorageDisclosureUrl)
          .then(response => response.json())
          .then(json => (this.deviceStorageDisclosures = json.disclosures));
      }
      this.displayMore = !this.displayMore;
    },
  },
};
</script>
<style lang="less">
.visibility-hidden {
  visibility: hidden;
  .btn-toggle.default-neutral > span {
    visibility: hidden;
  }
}

.detail-margin {
  //when there is no ul
  margin-bottom: 0.6rem;
}

.tc-storage-detail {
  margin-top: 2rem;
  font-weight: 600;
}

.pl-0 {
  padding-left: 0px;
}
</style>
